import type {
    ApiLoopAutocompleteRequest,
    ApiLoopBasketRecommendationsRequest,
    ApiLoopComplementaryRecommendationsRequest,
    ApiLoopCurrentOffersRequest,
    ApiLoopGlobalSearchRequest,
    ApiLoopPopularProductsRequest,
    ApiLoopPopularRecipesRequest,
    ApiLoopProduct,
    ApiLoopRangeFacet,
    ApiLoopRecentProductsRequest,
    ApiLoopResultOptions,
    ApiLoopSearchByAttributeRequest,
    ApiLoopSearchEntitiesRequest,
    ApiLoopSearchRequest,
} from '../api/searchRequests';
import type {
    BaseSearchRequest,
    BasketRecommendationsRequest,
    ComplementaryRecommendationsRequest,
    GlobalSearchRequest,
    GlobalSearchResults,
    LoopNoQueryProductResults,
    LoopSearchProductResults,
    PointshopSearchAndRequest,
    PointshopSearchRequest,
    PopularProductsRequest,
    PopularRecipesRequest,
    RecentRecommendationsRequest,
    RecipeSearchRequest,
    SearchArticleResults,
    SearchByAttributeLoopRequest,
    SearchByAttributeRequest,
    SearchEntitiesRequest,
    SearchPointshop,
    SearchProductOffersLoopRequest,
    SearchProductsLoopRequest,
    SearchRecipeResult,
    SearchRequest,
    SearchRequestFacet,
    SearchStoreResults,
    AutocompleteFlowRequest,
} from '../models/models';

import { appConfig } from '../../../appConfig';
import { Browser } from '../../../utility/browser';
import { productApi } from '../../common/api/products/productApi';
import { DirectProductProvider } from '../../common/flow/product/directProductProvider';
import userFlow from '../../common/flow/user/userFlow';
import persistance from '../../common/persistance/persistance';
import { searchDirectApi } from '../api/searchApi';
import { SearchFacetType } from '../models/models';
import { buildDefaultPromotionsFilter } from '../models/promotionLoopFilters';
import { mapFacets, mapFilter, mapSortOrder } from './mapSearchRequest';
import {
    mapLoopArticleSearchResponse,
    mapLoopAutocompleteResponse,
    mapLoopGlobalSearchResponse,
    mapLoopProductNoQuerySearchResponse,
    mapLoopProductSearchResponse,
    mapLoopRecipeNoQuerySearchResponse,
    mapLoopSearchPointshop,
    mapLoopSearchRecipe,
    mapLoopStoreSearchResponse,
} from './mapSearchResponse';

const searchRecipesLoop = async ({
    searchRequest,
}: {
    searchRequest: RecipeSearchRequest;
}): Promise<SearchRecipeResult> => {
    const loopSearchRequest: ApiLoopSearchRequest = {
        query: searchRequest.searchQuery,
        resultsOptions: {
            skip: searchRequest.page * searchRequest.resultsPerPage,
            take: searchRequest.resultsPerPage,
            sortBy: searchRequest.sortBy || undefined,
            facets: mapFacets(searchRequest.facets),
            filter: {
                and: [
                    {
                        type: 'attribute',
                        attributeName: 'is_food_box',
                        value: false,
                        comparisonMode: 'equals',
                    },
                ],
            },
        },
    };

    const response = await searchDirectApi.searchRecipes({ loopSearchRequest });

    return {
        count: response.results.count,
        items: response.results.items.map(mapLoopSearchRecipe),
        facets: response.results.facets,
        queryUsed: response.queryUsed,
    };
};

const searchProductsLoop = async ({
    searchRequest,
    abortSignal,
    searchService,
    quickSearch, // used by loop to indicate if the search was used when you type, they need it for something....
    abTest,
}: SearchProductsLoopRequest): Promise<LoopSearchProductResults> => {
    const loopSearchRequest: ApiLoopSearchRequest = {
        query: searchRequest.searchTerm,
        resultsOptions: {
            skip: searchRequest.page * searchRequest.resultsPerPage,
            take: searchRequest.resultsPerPage,
            sortBy: mapSortOrder(searchRequest.sortBy),
            facets: mapFacets(searchRequest.facets),
            filter: searchRequest.filter,
        },
        relatedResultsOptions: {
            skip: 0,
            take: 16,
        },
    };

    if (abTest) {
        loopSearchRequest.customData = {
            searchABTest: true,
        };
    }

    const customerGroups = await userFlow.getB2BContextCustomerGroups();
    const device = Browser.platformType();
    const response = await searchDirectApi.searchProducts({
        loopSearchRequest,
        storeId: searchRequest.storeId,
        abortSignal,
        searchService,
        customerGroups,
        device,
        quickSearch,
    });
    const mappedResponse = mapLoopProductSearchResponse(response, searchRequest);
    return mappedResponse;
};

const searchProductOffersLoop = async ({
    searchRequest,
    abortSignal,
    groupOffers,
    overwritePersonalizeCampaigns,
}: SearchProductOffersLoopRequest) => {
    // EDLP exclusion is backend-side for this API. Callers may still pass a filter
    // (e.g. PromoListBlock Kampanjtyper); do not inject a default here.
    const loopSearchRequest: ApiLoopCurrentOffersRequest = {
        resultsOptions: {
            skip: searchRequest.page * searchRequest.resultsPerPage,
            take: searchRequest.resultsPerPage,
            sortBy: mapSortOrder(searchRequest.sortBy),
            facets: mapFacets(searchRequest.facets),
            filter: searchRequest.filter ? mapFilter(searchRequest.filter) : undefined,
        },
        customData: {
            personalizeCampaigns: !!overwritePersonalizeCampaigns,
        },
    };

    const customerGroups = await userFlow.getB2BContextCustomerGroups();
    const response = await productApi.getCurrentProductOffersLoop({
        currentPromotionsRequest: loopSearchRequest,
        storeId: searchRequest.storeId,
        abortSignal,
        customerGroups,
        onlyPrimary: groupOffers,
    });

    const mappedResponse = mapLoopProductNoQuerySearchResponse(response);
    return mappedResponse;
};

const searchArticlesLoop = async (
    searchRequest: SearchRequest,
    abortSignal: AbortSignal,
): Promise<SearchArticleResults> => {
    const loopSearchRequest: ApiLoopSearchRequest = {
        query: searchRequest.searchTerm,
        resultsOptions: {
            skip: searchRequest.page * searchRequest.resultsPerPage,
            take: searchRequest.resultsPerPage,
            sortBy: mapSortOrder(searchRequest.sortBy),
            facets: mapFacets(searchRequest.facets),
        },
    };

    const response = await searchDirectApi.searchArticles(loopSearchRequest, abortSignal);
    const mappedResponse = mapLoopArticleSearchResponse(response, searchRequest);
    return mappedResponse;
};

const searchStoresLoop = async (
    searchRequest: SearchRequest,
    abortSignal: AbortSignal,
): Promise<SearchStoreResults> => {
    const loopSearchRequest: ApiLoopSearchRequest = {
        query: searchRequest.searchTerm,
        resultsOptions: {
            skip: searchRequest.page * searchRequest.resultsPerPage,
            take: searchRequest.resultsPerPage,
            sortBy: mapSortOrder(searchRequest.sortBy),
            facets: mapFacets(searchRequest.facets),
        },
    };

    const response = await searchDirectApi.searchStores(loopSearchRequest, abortSignal);
    const mappedResponse = mapLoopStoreSearchResponse(response, searchRequest);
    return mappedResponse;
};

const searchLoyaltyProductsById = async (ids: number[]): Promise<SearchPointshop[]> => {
    if (!ids || !ids.length) {
        return [];
    }
    const distinctIds = ids.filter((id, index, result) => id && index === result.indexOf(id));
    const response = await searchDirectApi.getLoyaltyProductsById(distinctIds);

    const mappedResponse = response.results.items
        .filter((x) => x.type === 'PartneroffersEntity' || x.type === 'PointshopEntity')
        .map(mapLoopSearchPointshop);
    return mappedResponse;
};

const searchLoyaltyProducts = async (
    searchRequest: PointshopSearchAndRequest,
): Promise<SearchPointshop[]> => {
    const loopSearchRequest: ApiLoopSearchRequest = {
        query: '',
        resultsOptions: {
            skip: searchRequest.page * searchRequest.resultsPerPage,
            take: searchRequest.resultsPerPage,
            sortBy: mapSortOrder(searchRequest.sortBy),
            facets: mapFacets(searchRequest.facets),
            filter: searchRequest.filter && mapFilter(searchRequest.filter),
        },
    };
    const response = await searchDirectApi.searchLoyaltyProducts({ loopSearchRequest });

    const mappedResponse = response.results.items
        .filter((x) => x.type === 'PartneroffersEntity' || x.type === 'PointshopEntity')
        .map(mapLoopSearchPointshop);
    return mappedResponse;
};

const mapResultOptions = (searchRequest: BaseSearchRequest): ApiLoopResultOptions => ({
    skip: searchRequest.page * searchRequest.resultsPerPage,
    take: searchRequest.resultsPerPage,
    sortBy: mapSortOrder(searchRequest.sortBy), // this will probably be different per document type, if we ever use it
    facets: mapFacets(searchRequest.facets), // this will probably be different per document type, if we ever use it
});

const mapResultFilterOptions = (searchRequest: PointshopSearchRequest): ApiLoopResultOptions => ({
    skip: searchRequest.page * searchRequest.resultsPerPage,
    take: searchRequest.resultsPerPage,
    sortBy: mapSortOrder(searchRequest.sortBy), // this will probably be different per document type, if we ever use it
    facets: mapFacets(searchRequest.facets), // this will probably be different per document type, if we ever use it
    filter: searchRequest.filter && mapFilter(searchRequest.filter),
});

const searchGlobalLoop = async (
    searchRequest: GlobalSearchRequest,
    quickSearch: boolean,
    abortSignal?: AbortSignal,
): Promise<GlobalSearchResults> => {
    const loopGlobalSearchRequest: ApiLoopGlobalSearchRequest = {
        query: searchRequest.searchTerm,
    };

    if (searchRequest.productSearchRequest) {
        loopGlobalSearchRequest.resultsOptions = mapResultOptions(
            searchRequest.productSearchRequest,
        );
    } else {
        // we dont want products data when no productSearchRequest is provided
        loopGlobalSearchRequest.resultsOptions = {
            skip: 0,
            take: 0,
        };
    }

    if (searchRequest.storeSearchRequest) {
        loopGlobalSearchRequest.storesResultsOptions = mapResultOptions(
            searchRequest.storeSearchRequest,
        );
    }

    if (searchRequest.articleSearchRequest) {
        loopGlobalSearchRequest.editorialContentResultsOptions = mapResultOptions(
            searchRequest.articleSearchRequest,
        );
    }

    if (searchRequest.pointshopSearchRequest) {
        loopGlobalSearchRequest.pointshopResultsOptions = mapResultFilterOptions(
            searchRequest.pointshopSearchRequest,
        );
    }

    if (searchRequest.recipeSearchRequest) {
        loopGlobalSearchRequest.recipesResultsOptions = mapResultOptions(
            searchRequest.recipeSearchRequest,
        );

        loopGlobalSearchRequest.recipesResultsOptions.filter = {
            and: [
                {
                    type: 'attribute',
                    attributeName: 'recipes_is_food_box',
                    value: false,
                    comparisonMode: 'equals',
                },
            ],
        };
    }

    if (searchRequest.recipeCategoriesSearchRequest) {
        loopGlobalSearchRequest.recipeCategoriesResultsOptions = mapResultOptions(
            searchRequest.recipeCategoriesSearchRequest,
        );
    }

    const customerGroups = await userFlow.getB2BContextCustomerGroups();

    const device = Browser.platformType();
    const response = await searchDirectApi.searchGlobal({
        loopGlobalSearchRequest,
        storeId: searchRequest.storeId,
        abortSignal,
        customerGroups,
        device,
        quickSearch,
    });
    const mappedResponse = mapLoopGlobalSearchResponse(response, searchRequest.searchTerm);
    return mappedResponse;
};

const searchByAttribute = async (
    searchRequest: SearchByAttributeRequest,
    abortSignal?: AbortSignal,
): Promise<LoopNoQueryProductResults> => {
    const loopSearchRequest: ApiLoopSearchByAttributeRequest = {
        attribute: searchRequest.attribute,
        requestAlias: searchRequest.requestAlias
            ? {
                  name: searchRequest.requestAlias.name,
                  value: searchRequest.requestAlias.value,
                  details: searchRequest.requestAlias.details,
              }
            : undefined,
        resultsOptions: {
            skip: searchRequest.page * searchRequest.resultsPerPage,
            take: searchRequest.resultsPerPage,
            sortBy: mapSortOrder(searchRequest.sortBy),
            facets: mapFacets(searchRequest.facets),
            filter: searchRequest.filter,
        },
        customData: {
            getEntitiesByAttributeABTest:
                searchRequest.customData?.getEntitiesByAttributeABTest || false,
        },
    };

    const customerGroups = await userFlow.getB2BContextCustomerGroups();

    const device = Browser.platformType();
    const response = await searchDirectApi.searchByAttribute({
        loopSearchRequest,
        storeId: searchRequest.storeId,
        customerGroups,
        device,
        abortSignal,
    });

    DirectProductProvider.instance.insertItems(response.results.items, searchRequest.storeId);
    const mappedResponse = mapLoopProductNoQuerySearchResponse(response);

    return mappedResponse;
};

const searchEntities = async (
    searchRequest: SearchEntitiesRequest,
    abortSignal?: AbortSignal,
): Promise<LoopNoQueryProductResults> => {
    const loopSearchRequest: ApiLoopSearchEntitiesRequest = {
        resultsOptions: {
            skip: searchRequest.skip,
            take: searchRequest.take,
            sortBy: mapSortOrder(searchRequest.sortBy),
            facets: searchRequest.facets ? mapFacets(searchRequest.facets) : undefined,
            filter: searchRequest.filter && mapFilter(searchRequest.filter),
        },
    };

    const customerGroups = await userFlow.getB2BContextCustomerGroups();
    const device = Browser.platformType();
    const response = await searchDirectApi.searchEntities({
        loopSearchRequest,
        storeId: searchRequest.storeId,
        customerGroups,
        device,
        abortSignal,
    });

    DirectProductProvider.instance.insertItems(response.results.items, searchRequest.storeId);
    return mapLoopProductNoQuerySearchResponse(response);
};

const getBasketRecommendations = async (recommendationsRequest: BasketRecommendationsRequest) => {
    const loopRequest: ApiLoopBasketRecommendationsRequest = {
        basket: recommendationsRequest.ids.map<ApiLoopProduct>((id) => ({
            type: 'Product',
            id,
        })),
        resultsOptions: {
            skip: recommendationsRequest.page * recommendationsRequest.resultsPerPage,
            take: recommendationsRequest.resultsPerPage,
            sortBy: mapSortOrder(recommendationsRequest.sortBy),
            facets: mapFacets(recommendationsRequest.facets),
        },
    };
    const customerGroups = await userFlow.getB2BContextCustomerGroups();

    const device = Browser.platformType();
    const response = await searchDirectApi.basketRecommendations({
        loopSearchRequest: loopRequest,
        storeId: recommendationsRequest.storeId,
        customerGroups,
        device,
    });
    const mappedResponse = mapLoopProductNoQuerySearchResponse(response);
    return mappedResponse;
};

const getPopularProducts = async (popularProductRequest: PopularProductsRequest) => {
    const loopRequest: ApiLoopPopularProductsRequest = {
        entityType: ['Product'],
        behaviorType: popularProductRequest.behaviorType,
        resultsOptions: {
            skip: popularProductRequest.page * popularProductRequest.resultsPerPage,
            take: popularProductRequest.resultsPerPage,
            sortBy: mapSortOrder(popularProductRequest.sortBy),
            facets: mapFacets(popularProductRequest.facets),
            filter: popularProductRequest.filter,
        },
    };

    if (popularProductRequest.personalizedForUser) {
        loopRequest.forUserId = '(CurrentUser)';
    }

    const customerGroups = await userFlow.getB2BContextCustomerGroups();

    const device = Browser.platformType();
    const response = await searchDirectApi.popularProducts({
        loopSearchRequest: loopRequest,
        storeId: popularProductRequest.storeId,
        customerGroups,
        device,
    });
    const mappedResponse = mapLoopProductNoQuerySearchResponse(response);
    return mappedResponse;
};

const getComplementaryRecommendations = async (
    complementaryRecommendationsRequest: ComplementaryRecommendationsRequest,
) => {
    const loopRequest: ApiLoopComplementaryRecommendationsRequest = {
        entity: {
            type: 'Product',
            id: complementaryRecommendationsRequest.productId,
        },
        resultsOptions: {
            skip:
                complementaryRecommendationsRequest.page *
                complementaryRecommendationsRequest.resultsPerPage,
            take: complementaryRecommendationsRequest.resultsPerPage,
            sortBy: mapSortOrder(complementaryRecommendationsRequest.sortBy),
            facets: mapFacets(complementaryRecommendationsRequest.facets),
        },
    };
    const customerGroups = await userFlow.getB2BContextCustomerGroups();

    const device = Browser.platformType();
    const response = await searchDirectApi.complementaryProducts({
        loopSearchRequest: loopRequest,
        storeId: complementaryRecommendationsRequest.storeId,
        customerGroups,
        device,
    });
    const mappedResponse = mapLoopProductNoQuerySearchResponse(response);
    return mappedResponse;
};

const getRecentRecommendations = async (
    complementaryRecommendationsRequest: RecentRecommendationsRequest,
) => {
    const hasId = !!persistance.loop54UserSession.get() || !!appConfig.coopUserSettings.userId;
    if (!hasId) {
        return {
            totalResults: 0,
            items: [],
        };
    }
    const loopRequest: ApiLoopRecentProductsRequest = {
        entityType: ['Product'],
        forUserId: '(CurrentUser)',
        behaviorType: complementaryRecommendationsRequest.behaviorType,
        resultsOptions: {
            skip:
                complementaryRecommendationsRequest.page *
                complementaryRecommendationsRequest.resultsPerPage,
            take: complementaryRecommendationsRequest.resultsPerPage,
            sortBy: mapSortOrder(complementaryRecommendationsRequest.sortBy),
            facets: mapFacets(complementaryRecommendationsRequest.facets),
        },
    };
    const customerGroups = await userFlow.getB2BContextCustomerGroups();

    const device = Browser.platformType();
    const response = await searchDirectApi.recentRecommendations({
        loopSearchRequest: loopRequest,
        storeId: complementaryRecommendationsRequest.storeId,
        customerGroups,
        device,
    });
    const mappedResponse = mapLoopProductNoQuerySearchResponse(response);
    return mappedResponse;
};

const searchByAttributeLoop = async ({
    attribute,
    resultsPerPage,
    selectedPage,
    selectedBrand,
    selectedSortOrder,
    selectedEnvironmentalLabels,
    storeId,
    personalizeProductsByAttribute,
    requestAlias,
    onlinePromotionsOnly,
    selectedTopCategory,
    abortSignal,
}: SearchByAttributeLoopRequest) => {
    const sortBy = selectedSortOrder ? [selectedSortOrder] : [];
    const facets: SearchRequestFacet[] = [
        {
            type: SearchFacetType.Brand,
            selected: selectedBrand || [],
        },
        {
            type: SearchFacetType.EnvironmentalLabels,
            operator: 'OR',
            selected: selectedEnvironmentalLabels || [],
        },
        {
            type: SearchFacetType.TopCategory,
            operator: 'OR',
            selected: selectedTopCategory || [],
        },
    ];

    const currentPage = selectedPage > 0 ? selectedPage - 1 : 0;

    return searchByAttribute(
        {
            attribute,
            sortBy,
            resultsPerPage,
            facets,
            page: currentPage,
            storeId,
            customData: {
                getEntitiesByAttributeABTest: personalizeProductsByAttribute || false,
            },
            requestAlias,
            filter: onlinePromotionsOnly ? buildDefaultPromotionsFilter() : undefined,
        },
        abortSignal,
    );
};

const autocomplete = async ({
    searchTerm,
    skip = 0,
    take = 5,
    abortSignal,
    searchService,
    storeId,
}: AutocompleteFlowRequest) => {
    const loopAutocompleteRequest: ApiLoopAutocompleteRequest = {
        query: searchTerm,
        queriesOptions: { skip, take },
    };

    const customerGroups = await userFlow.getB2BContextCustomerGroups();

    const device = Browser.platformType();

    const response = await searchDirectApi.autoComplete({
        loopAutocompleteRequest,
        abortSignal,
        storeId,
        searchService,
        customerGroups,
        device,
    });

    const mappedResponse = mapLoopAutocompleteResponse(response, searchTerm);
    return mappedResponse;
};

const searchProductsInSection = async (
    request: Omit<SearchByAttributeLoopRequest, 'abortSignal'>,
): Promise<LoopNoQueryProductResults> => {
    const loopResults = await searchByAttributeLoop(request);

    return loopResults;
};

const searchPointshopProductsInPointRange = async (
    minPointsValue: number,
    maxPointsValue: number,
    searchRequest: {
        page: number;
        resultsPerPage: number;
    },
) => {
    const rangeFacet: ApiLoopRangeFacet = {
        attributeName: 'pointPrice',
        type: 'range',
        selected: {
            min: minPointsValue,
            max: maxPointsValue,
        },
    };

    const loopSearchRequest: ApiLoopSearchRequest = {
        query: '',
        resultsOptions: {
            skip: searchRequest.page * searchRequest.resultsPerPage,
            take: searchRequest.resultsPerPage,
            facets: [rangeFacet],
        },
    };

    const response = await searchDirectApi.searchLoyaltyProducts({ loopSearchRequest });
    const mapped = response.results.items
        .filter((x) => x.type === 'PointshopEntity')
        .map(mapLoopSearchPointshop);

    return mapped;
};

const getRecipeRecommendations = async (
    forCurrentUser: boolean,
    recipeRequest: PopularRecipesRequest,
) => {
    const loopRequest: ApiLoopPopularRecipesRequest = {
        behaviorType: recipeRequest.behaviorType,
        forUserId: forCurrentUser ? '(CurrentUser)' : undefined,
        resultsOptions: {
            skip: recipeRequest.page * recipeRequest.resultsPerPage,
            take: recipeRequest.resultsPerPage,
        },
    };

    const response = await searchDirectApi.fetchRecipeRecommendations(loopRequest);
    return mapLoopRecipeNoQuerySearchResponse(response);
};

const getRecipeRecent = async (forCurrentUser: boolean, recipeRequest: PopularRecipesRequest) => {
    const loopRequest: ApiLoopPopularRecipesRequest = {
        behaviorType: recipeRequest.behaviorType,
        forUserId: forCurrentUser ? '(CurrentUser)' : undefined,
        resultsOptions: {
            skip: recipeRequest.page * recipeRequest.resultsPerPage,
            take: recipeRequest.resultsPerPage,
        },
    };

    const response = await searchDirectApi.fetchRecipeRecent(loopRequest);
    return mapLoopRecipeNoQuerySearchResponse(response);
};

const getRecipePopular = async (forCurrentUser: boolean, recipeRequest: PopularRecipesRequest) => {
    const loopRequest: ApiLoopPopularRecipesRequest = {
        behaviorType: recipeRequest.behaviorType,
        forUserId: forCurrentUser ? '(CurrentUser)' : undefined,
        resultsOptions: {
            skip: recipeRequest.page * recipeRequest.resultsPerPage,
            take: recipeRequest.resultsPerPage,
        },
    };

    const response = await searchDirectApi.fetchRecipePopular(loopRequest);
    return mapLoopRecipeNoQuerySearchResponse(response);
};

export type { SearchService } from '../models/models';

const searchFlow = {
    searchProductsLoop,
    searchProductOffersLoop,
    searchArticlesLoop,
    searchStoresLoop,
    searchGlobalLoop,
    searchByAttribute,
    searchEntities,
    autocomplete,
    searchProductsInSection,
    searchByAttributeLoop,
    searchRecipesLoop,
    searchLoyaltyProductsById,
    searchLoyaltyProducts,
    searchPointshopProductsInPointRange,
    getBasketRecommendations,
    getPopularProducts,
    getComplementaryRecommendations,
    getRecentRecommendations,
    getRecipeRecommendations,
    getRecipeRecent,
    getRecipePopular,
};

export default searchFlow;
